// Thin fetch wrapper for the CRM API (proxied under /api in development).
import { note } from "./capture.ts";

export class ApiError extends Error {
	readonly status: number;
	readonly code: string;
	constructor(status: number, code: string, message: string) {
		super(message);
		this.status = status;
		this.code = code;
	}
}

export async function api<T>(
	path: string,
	init: {
		method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
		body?: unknown;
		/** Extra headers, like a link page's x-link-token. */
		headers?: Record<string, string>;
	} = {},
): Promise<T> {
	const method = init.method ?? (init.body === undefined ? "GET" : "POST");
	const res = await fetch(`/api${path}`, {
		method,
		credentials: "same-origin",
		headers: { ...(init.body === undefined ? {} : { "content-type": "application/json" }), ...init.headers },
		...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
	}).catch((err) => {
		note("request", `${method} ${path.split("?")[0]} didn't reach the server: ${(err as Error).message}`);
		throw err;
	});
	const data = res.status === 204 ? null : await res.json().catch(() => null);
	if (!res.ok) {
		// For bug reports: what failed and its request id, never what was sent.
		const id = res.headers.get("x-request-id");
		note("request", `${method} ${path.split("?")[0]} ${res.status} ${data?.code ?? ""}${id ? ` (request ${id})` : ""}`);
		// Prefer the specific reason ("This file type is not accepted") over a generic one.
		const message = data?.issues?.[0]?.message ?? data?.message ?? "Something went wrong";
		throw new ApiError(res.status, data?.code ?? "error", message);
	}
	return data as T;
}

export interface Me {
	user: { id: string; email: string; name: string };
	activeAgencyId: string | null;
	agencies: { id: string; name: string; slug: string }[];
	permissions: string[];
	isPlatformAdmin: boolean;
	viaPlatform: boolean;
	/** Why the agency they were working in has gone (paused or removed by SSG), in words. */
	stopped: string | null;
	/** A new sign-in email waiting for its link to be tapped. */
	pendingEmail: { email: string; sentAt: string; expiresAt: string } | null;
}

export interface FileInfo {
	id: string;
	name: string;
	contentType: string;
	size: number | null;
	status: "pending_upload" | "pending_scan" | "clean" | "infected" | "failed";
	variants: Record<string, { width?: number; height?: number; bytes?: number }>;
	urls: Record<string, string>;
}

export const meQuery = {
	queryKey: ["me"] as const,
	queryFn: async (): Promise<Me | null> => {
		try {
			return await api<Me>("/me");
		} catch (err) {
			if (err instanceof ApiError && err.status === 401) return null;
			throw err;
		}
	},
	staleTime: 30_000,
};

export interface Page<T> {
	items: T[];
	nextCursor: string | null;
	total: { count: number; capped: boolean };
}

export interface CandidateRow {
	id: string;
	ref: string;
	first_name: string;
	last_name: string;
	preferred_name: string | null;
	status: string;
	mobile: string | null;
	email: string | null;
	town: string | null;
	postcode: string | null;
	employment_type: string;
	compliance_status: string;
	compliance_missing: string[];
	is_bookable: boolean;
	last_contact_at: string | null;
	primary_job_title: string | null;
	consultant_name: string | null;
}

export interface Activity {
	id: string;
	kind: "note" | "call" | "email" | "sms" | "meeting" | "system";
	direction: string | null;
	subject: string | null;
	body: string | null;
	outcome: string | null;
	created_at: string;
	by: string | null;
}

export interface Candidate extends CandidateRow {
	phone: string | null;
	address_line1: string | null;
	address_line2: string | null;
	county: string | null;
	consultant_id: string | null;
	worker_type: string | null;
	registered_at: string | null;
	activities: Activity[];
	tasks: { id: string; title: string; priority: string; status: string; due_at: string | null }[];
}

export const consultantsQuery = {
	queryKey: ["consultants"] as const,
	queryFn: () => api<{ id: string; name: string }[]>("/consultants"),
	staleTime: 5 * 60_000,
};

export interface ClientRow {
	id: string;
	ref: string;
	name: string;
	status: string;
	sector: string | null;
	phone: string | null;
	town: string | null;
	postcode: string | null;
	last_contact_at: string | null;
	last_booking_at: string | null;
	consultant_name: string | null;
}

export interface ClientSite {
	id: string;
	ref: string;
	name: string;
	address_line1: string | null;
	address_line2: string | null;
	town: string | null;
	postcode: string | null;
	is_default: boolean;
}

export interface ClientContact {
	id: string;
	ref: string;
	first_name: string;
	last_name: string;
	job_title: string | null;
	email: string | null;
	phone: string | null;
	mobile: string | null;
	roles: string[];
	site_id: string | null;
}

export interface Client extends ClientRow {
	email: string | null;
	website: string | null;
	address_line1: string | null;
	address_line2: string | null;
	county: string | null;
	lead_consultant_id: string | null;
	credit_limit_pence: number | null;
	payment_terms_days: number | null;
	sites: ClientSite[];
	contacts: ClientContact[];
	activities: Activity[];
}
